import React, { useState } from "react";
import { Form, Upload, App, Spin } from "antd";
import { FilePdfOutlined, LoadingOutlined } from "@ant-design/icons";
import { UIButton } from "../../../components/ui/UIElements";
import { uploadFile } from "../../../pages/api/modules/User";

const toUploadList = (value) => {
  if (Array.isArray(value)) {
    return value.map((file, index) => {
      if (file && typeof file === "object") {
        return {
          uid: file.uid || `-${index + 1}`,
          name: file.name || file.FileName || `Document ${index + 1}`,
          status: "done",
          serverValue: file,
        };
      }
      return {
        uid: `-${index + 1}`,
        name: file.split("/").pop() || file,
        status: "done",
        serverValue: file
      };
    });
  }
  if (typeof value === "string" && value) {
    const fileName = value.split("/").pop() || value;
    return [{ uid: "-1", name: fileName, status: "done", serverValue: value }];
  }
  return [];
};

const UIFormAttachments = ({
  label,
  required,
  rules,
  onChange = () => {},
  validateStatus,
  hasFeedback,
  help,
  disabled,
  name,
  defaultValue,
  accept,
  multiple = false,
  maxCount = 1,
  resumeStyle = false,
  uploadTitle = "Attachments"
}) => {
  const { message } = App.useApp();
  const form = Form.useFormInstance(); // antd v5
  const [loading, setLoading] = useState(false);

  // Local Upload UI state (array of UploadFile)
  const [fileList, setFileList] = useState(() => toUploadList(defaultValue));

  const onUploadFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    setLoading(true);
    try {
      const res = await uploadFile(formData);
      if (res?.Status === 1) {
        const fileName = res?.Data?.Files?.FileName;
        file.serverValue = fileName;
        const currentValue = form.getFieldValue(name);
        const nextValue = multiple
          ? [...(Array.isArray(currentValue) ? currentValue : currentValue ? [currentValue] : []), fileName]
          : fileName;

        form.setFieldValue(name, nextValue);
        onChange(nextValue);
        setFileList((currentList) => {
          if (!multiple) return toUploadList(fileName);
          return currentList.map((item) =>
            item.uid === file.uid
              ? { ...item, status: "done", serverValue: fileName }
              : item
          );
        });

        message.success("File uploaded successfully");
      } else {
        message.error(res?.Message || "Something went wrong");
      }
    } catch {
      message.error("Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    accept,
    maxCount,
    multiple,
    disabled,
    fileList,
    openFileDialogOnClick: fileList.length < maxCount,
    beforeUpload: (file, selectedFiles) => {
      if (fileList.length + selectedFiles.length > maxCount) {
        message.open({
          key: `attachments-limit-${name}`,
          type: "warning",
          content: `A maximum of ${maxCount} attachments is allowed. Remove an existing attachment before adding another.`,
        });
        return Upload.LIST_IGNORE;
      }
      return true;
    },
    onChange: ({ fileList: nextFileList }) => {
      setFileList(nextFileList);
      const uploadedFiles = nextFileList
        .map((file) => file.serverValue)
        .filter(Boolean);
      const nextValue = multiple ? uploadedFiles : (uploadedFiles[0] || "");
      form.setFieldValue(name, nextValue);
      onChange(nextValue);
    },
    customRequest: ({ file, onSuccess, onError }) => {
      onUploadFile(file)
        .then(() => onSuccess?.("ok"))
        .catch((e) => onError?.(e));
    }
  };

  return (
    <>
      {/* Visible item for label + Upload (NOT bound to name) */}
      <Form.Item
        label={resumeStyle ? null : label}
        required={required}
        rules={rules}
        hasFeedback={hasFeedback}
        help={help}
        validateStatus={validateStatus}
      >
        {resumeStyle ? (
          <Upload.Dragger {...uploadProps}>
            {loading ? (
              <Spin indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />} />
            ) : (
              <p className="ant-upload-drag-icon"><FilePdfOutlined /></p>
            )}
            <p className="ant-upload-text">
              Cover Letter/Documents {!required && <span style={{ fontSize: 14, color: "grey" }}>(Optional)</span>}
            </p>
            <p className="ant-upload-hint">
              Click or drag files to this area to upload<br />
              Supported formats: PDF and DOCX. Maximum {maxCount} files.
            </p>
          </Upload.Dragger>
        ) : (
          <Upload {...uploadProps}>
            <UIButton type="default" loading={loading}>
              {loading ? "Uploading" : "Add Attachment"}
            </UIButton>
          </Upload>
        )}
      </Form.Item>

      {/* Hidden item actually bound to the form value (string) */}
      <Form.Item name={name} initialValue={defaultValue} hidden>
        <input />
      </Form.Item>
    </>
  );
};

export default UIFormAttachments;
